// Lệnh chuyển gộp tiền ship (docs/KE_HOACH_SHOP_TRA_SHIP_QR.md §6): shop chọn nhiều đơn "Chờ duyệt ship"
// ở trang Đơn hàng → một lệnh, một QR tổng tiền (nội dung "SHIPG <mã lệnh>"). Trang này: danh sách lệnh
// (/ship-payments) và chi tiết lệnh (/ship-payments/:id) — QR, các đơn trong lệnh, bỏ gộp khi còn chờ.

import { useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ArrowLeft, CheckCircle2, HandCoins, Layers, XCircle } from "lucide-react"
import { get, post } from "@/lib/api"
import { money, shortOrderId } from "@/lib/format"
import { Button } from "@/components/ui/button"
import { PageHeader } from "@/components/ui/page-header"
import { StatusPill } from "@/components/ui/status"
import { IndexBadge } from "@/components/ui/data-table"
import { QrTransferBox, type ShipBatchRef } from "@/components/orders/ShipPaymentPanel"
import { useNppName } from "@/components/layout/shell-model"

const BATCH_STATUS: Record<ShipBatchRef["status"], string> = {
  pending: "Chờ duyệt",
  approved: "Đã duyệt",
  debt: "Cho nợ",
  rejected: "Từ chối",
  cancelled: "Đã bỏ gộp",
}

const ITEM_STATUS: Record<string, string> = { ...BATCH_STATUS, cancelled: "Đã hủy" }

const when = (iso: string) => new Date(iso).toLocaleString("vi-VN")

interface BatchRow extends ShipBatchRef {
  createdAt: string
  decidedAt: string | null
  decisionNote: string | null
}

/** /ship-payments — các lệnh chuyển gộp của shop (mới nhất trước). */
export function ShipPaymentsPage() {
  const q = useQuery({
    queryKey: ["ship-batches"],
    queryFn: () => get<{ batches: BatchRow[]; waiting: { count: number; amount: number } }>("/ship-payments/batches"),
    refetchInterval: 30_000,
  })
  const batches = q.data?.batches ?? []
  const waiting = q.data?.waiting
  return (
    <div className="space-y-5">
      <PageHeader title="Tiền ship" />
      {waiting && waiting.count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent/5 px-4 py-3 text-sm text-ink">
          <span>
            {waiting.count} đơn đang chờ chuyển tiền ship riêng · {money(waiting.amount)}
          </span>
          <Link to="/orders?status=cho-duyet" className="font-medium text-accent hover:underline">
            Chuyển gộp →
          </Link>
        </div>
      )}
      <div data-surface="solid" className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isLoading ? (
          <p className="px-4 py-6 text-center text-sm text-muted">Đang tải…</p>
        ) : !batches.length ? (
          <p className="px-4 py-6 text-center text-sm text-muted">Chưa có lệnh nào.</p>
        ) : (
          <ul className="divide-y divide-rule/60">
            {batches.map((b, i) => (
              <li key={b.id}>
                <Link to={`/ship-payments/${b.id}`} className="flex min-h-14 items-center gap-3 px-4 py-2.5 hover:bg-paperSoft">
                  <Layers className="size-4 shrink-0 text-accent" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <IndexBadge n={i + 1} />
                      <span className="truncate font-mono text-sm font-semibold text-ink">{b.transferContent}</span>
                    </span>
                    <span className="block text-xs text-muted">
                      {b.orderCount} đơn · gộp {when(b.createdAt)}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-mono text-sm font-semibold tabular-nums text-ink">{money(b.amount)}</span>
                    <span className="block text-xs text-inkSoft">{BATCH_STATUS[b.status] ?? b.status}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

interface BatchDetail {
  batch: ShipBatchRef & {
    bankName: string | null
    accountNumber: string | null
    accountName: string | null
    qrDataUrl: string | null
    decisionNote: string | null
    decidedAt: string | null
    createdAt: string
  }
  items: Array<{
    orderId: string
    clientOrderId: string
    amount: number
    status: string
    decisionNote: string | null
    orderStatus: string | null
    cod: number | null
    customerName: string | null
    shipmentCode: string | null
  }>
  readOnly: boolean
}

/** /ship-payments/:id — QR tổng + các đơn trong lệnh. */
export function ShipPaymentBatchPage() {
  const { id = "" } = useParams()
  const [params] = useSearchParams()
  const ownerShopId = params.get("shopId")
  const navigate = useNavigate()
  const qc = useQueryClient()
  const nppName = useNppName()
  const [cancelling, setCancelling] = useState(false)
  const q = useQuery({
    queryKey: ["ship-batch", id, ownerShopId],
    queryFn: () => get<BatchDetail>(`/ship-payments/batches/${encodeURIComponent(id)}${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`),
    retry: false,
    // Đang chờ: hỏi lại để thấy ngay lúc được duyệt
    refetchInterval: (query) => (query.state.data?.batch.status === "pending" ? 15_000 : false),
  })
  const d = q.data
  const orderHref = (orderId: string) => `/orders/${encodeURIComponent(orderId)}${ownerShopId ? `?shopId=${encodeURIComponent(ownerShopId)}` : ""}`

  async function ungroup() {
    if (!window.confirm("Bỏ gộp lệnh này? Chỉ bỏ gộp khi bạn CHƯA chuyển khoản theo lệnh.")) return
    setCancelling(true)
    try {
      await post(`/ship-payments/batches/${encodeURIComponent(id)}/cancel`)
      toast.success("Đã bỏ gộp")
      qc.invalidateQueries({ queryKey: ["orders"] })
      qc.invalidateQueries({ queryKey: ["ship-batches"] })
      navigate("/orders?status=cho-duyet")
    } catch (err) {
      toast.error((err as Error).message)
      q.refetch()
    } finally {
      setCancelling(false)
    }
  }

  if (q.isLoading) return <p className="px-4 py-6 text-center text-sm text-muted">Đang tải lệnh…</p>
  if (!d) return <p className="px-4 py-6 text-center text-sm text-muted">{(q.error as Error | null)?.message ?? "Không tìm thấy lệnh chuyển gộp"}</p>
  const b = d.batch

  return (
    <div className="space-y-5">
      <PageHeader
        title={`Lệnh ${b.transferContent}`}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/ship-payments">
              <ArrowLeft aria-hidden />
              Các lệnh
            </Link>
          </Button>
        }
      />

      {b.status === "pending" ? (
        <QrTransferBox
          title={`Chuyển một lần tiền ship cho ${b.orderCount} đơn`}
          amount={b.amount}
          transferContent={b.transferContent}
          qrDataUrl={b.qrDataUrl}
          bankName={b.bankName}
          accountNumber={b.accountNumber}
          accountName={b.accountName}
          highlight
          footer={
            <>
              Giữ <b>đúng số tiền và nội dung</b> khi chuyển.
            </>
          }
        />
      ) : (
        <div className="flex items-start gap-2 rounded-lg border border-rule bg-paperSoft px-3 py-2.5 text-sm text-ink">
          {b.status === "approved" ? (
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
          ) : b.status === "debt" ? (
            <HandCoins className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          ) : (
            <XCircle className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
          )}
          <span>
            {b.status === "approved" && <>{nppName} đã nhận <b>{money(b.amount)}</b> tiền ship — các đơn đã được gửi sang hãng vận chuyển.</>}
            {b.status === "debt" && <>{nppName} cho nợ <b>{money(b.amount)}</b> tiền ship — các đơn đã được gửi sang hãng vận chuyển.</>}
            {b.status === "rejected" && <>{nppName} chưa xác nhận tiền ship{b.decisionNote ? `: ${b.decisionNote}` : ""}. Các đơn đã hủy, tồn kho đã trả lại.</>}
            {b.status === "cancelled" && <>Lệnh đã bỏ gộp — các đơn trở về chờ chuyển tiền ship riêng.</>}
          </span>
        </div>
      )}

      {d.items.length > 0 && (
        <section data-surface="solid" className="overflow-hidden rounded-xl bg-raise shadow-card" aria-labelledby="batch-orders">
          <h2 id="batch-orders" className="border-b border-rule px-4 py-3 text-sm font-semibold text-ink">
            {d.items.length} đơn trong lệnh
          </h2>
          <ul className="divide-y divide-rule/60">
            {d.items.map((it, i) => (
              <li key={it.orderId}>
                <Link to={orderHref(it.orderId)} className="flex min-h-14 items-center gap-3 px-4 py-2.5 hover:bg-paperSoft">
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <IndexBadge n={i + 1} />
                      <span className="truncate text-sm font-medium text-ink">{it.customerName ?? "—"}</span>
                    </span>
                    <span className="block truncate font-mono text-xs text-muted" title={it.orderId}>
                      {shortOrderId(it.orderId)}
                      {it.shipmentCode && ` · ${it.shipmentCode}`}
                    </span>
                  </span>
                  <span className="hidden shrink-0 sm:block">{it.orderStatus ? <StatusPill status={it.orderStatus} /> : <span className="text-xs text-muted">{ITEM_STATUS[it.status] ?? it.status}</span>}</span>
                  <span className="shrink-0 text-right">
                    <span className="block font-mono text-sm font-semibold tabular-nums text-ink">{money(it.amount)}</span>
                    {it.cod != null && <span className="block text-xs text-muted">COD {money(it.cod)}</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {b.status === "pending" && !d.readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-inkSoft">
          <Button variant="outline" size="sm" onClick={ungroup} disabled={cancelling}>
            {cancelling ? "Đang bỏ gộp…" : "Bỏ gộp"}
          </Button>
        </div>
      )}
    </div>
  )
}
